import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Crown } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { Dialog } from '@/components/ui/dialog'
import { Input, Select, Field } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useToast } from '@/providers/UiProviders'
import { fetchBotsForMod, setBotVipLimit, BOT_MOD_STR as S, type BotModRow } from './moderationApi'

const TIERS = [
  { value: 'standard', label: S.tierStandard, mult: 1 },
  { value: 'silver', label: S.tierSilver, mult: 2 },
  { value: 'gold', label: S.tierGold, mult: 5 },
  { value: 'platinum', label: S.tierPlatinum, mult: 10 },
]

/**
 * A5.15 — Bot limitini oshirish (VIP). LIST = REAL GET /admin/bots. VIP tarif tayinlash
 * → POST /admin/bots/:id/vip-limit (lane-A greenfield — deploy'gacha 404 → toast).
 * VIP tarif rate-limit + update-navbat cheklovlarini ko'paytiradi.
 */
export function VipLimitScreen() {
  const { toast } = useToast()
  const [target, setTarget] = useState<BotModRow | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const botLabel = (r: BotModRow) => (r.username ? `@${r.username}` : r.first_name || `#${r.id}`)

  const columns = useMemo<AdminColumn<BotModRow>[]>(
    () => [
      {
        key: 'username',
        header: S.bot,
        type: 'text',
        minWidth: 180,
        render: (r) => (
          <Link to={`/bots/${r.id}`} className="text-accent hover:underline">
            {botLabel(r)}
          </Link>
        ),
      },
      { key: 'first_name', header: S.name, type: 'text', minWidth: 160 },
      {
        key: 'vip_tier',
        header: S.vipTier,
        type: 'text',
        width: 140,
        render: (r) => {
          const tier = TIERS.find((tr) => tr.value === r.vip_tier)
          return <span className={r.vip_tier && r.vip_tier !== 'standard' ? 'text-accent' : 'text-tertiary'}>{tier ? tier.label : '—'}</span>
        },
      },
      {
        key: 'vip_multiplier',
        header: S.vipMultiplier,
        type: 'number',
        width: 130,
        align: 'right',
        render: (r) => <span className="tabular">{r.vip_multiplier ? `×${r.vip_multiplier}` : '—'}</span>,
      },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<BotModRow>[]>(
    () => [
      { id: 'vip', label: S.setVip, icon: <Crown size={13} />, onSelect: (r) => setTarget(r) },
    ],
    [],
  )

  const fetcher = useCallback(fetchBotsForMod, [])

  return (
    <>
      <PageHeader title={S.vipTitle} screenId="A5.15" />
      <DataTable<BotModRow>
        key={reloadKey}
        screenId="A5.15"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Crown size={28} strokeWidth={1.5} />, title: S.empty }}
      />

      {target && (
        <VipDialog
          row={target}
          onClose={() => setTarget(null)}
          onSaved={() => {
            setTarget(null)
            setReloadKey((k) => k + 1)
            toast(S.vipSaved, { tone: 'success' })
          }}
          onError={(e) =>
            toast(S.failed, {
              tone: 'error',
              ...(e instanceof Error && e.message ? { description: e.message } : {}),
            })
          }
        />
      )}
    </>
  )
}

function VipDialog({
  row,
  onClose,
  onSaved,
  onError,
}: {
  row: BotModRow
  onClose: () => void
  onSaved: () => void
  onError: (e: unknown) => void
}) {
  const [tier, setTier] = useState(row.vip_tier ?? 'standard')
  const [multiplier, setMultiplier] = useState(String(row.vip_multiplier ?? TIERS.find((t) => t.value === (row.vip_tier ?? 'standard'))?.mult ?? 1))
  const [busy, setBusy] = useState(false)

  const onTier = (v: string) => {
    setTier(v)
    const def = TIERS.find((t) => t.value === v)
    if (def) setMultiplier(String(def.mult))
  }

  const save = async () => {
    setBusy(true)
    try {
      await setBotVipLimit(row.id, { tier, multiplier: parseInt(multiplier, 10) || 1 })
      onSaved()
    } catch (e) {
      setBusy(false)
      onError(e)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${S.setVip} — ${row.username ? `@${row.username}` : `#${row.id}`}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {S.cancel}
          </Button>
          <Button onClick={save} disabled={busy}>
            {S.save}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-xs text-tertiary">{S.vipHint}</p>
        <Field label={S.vipTier}>
          <Select value={tier} onChange={(e) => onTier(e.target.value)}>
            {TIERS.map((tr) => (
              <option key={tr.value} value={tr.value}>
                {tr.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={S.vipMultiplier}>
          <Input value={multiplier} onChange={(e) => setMultiplier(e.target.value)} inputMode="numeric" />
        </Field>
      </div>
    </Dialog>
  )
}
